import {lazy} from 'react';
import sessionStore from 'client/Session/SessionStore';

import {
  createRoute,
  redirectRoute,
  layoutComponent,
  withRouter,
  guardRedirect
} from 'client/routeHelpers';

const SubjectSearch = lazy(() => import('./SubjectSearch/SubjectSearch'));
const AuthoringSubject = lazy(() => import('./AuthoringSubject/AuthoringSubject'));
const AuthoringSubjectWithQuery = lazy(
  () => import('./AuthoringSubject/AuthoringSubjectWithQuery')
);
const AuthoringSubjectEditor = lazy(
  () => import('./AuthoringSubject/AuthoringSubjectEditor/AuthoringSubjectEditor')
);
const GuideEditor = lazy(() => import('./Guides/GuideEditor/GuideEditor'));

const requireContentManager = guardRedirect(
  () => sessionStore.hasContentManagerAccess(),
  '/dennis/content/queue'
);

export function makeSubjectsRoute(parentRoute) {
  const subjectsRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: 'subjects',
    staticData: {name: 'Subject Manager'},
    beforeLoad: requireContentManager
  });

  const indexRedirect = redirectRoute({
    getParentRoute: () => subjectsRoute,
    path: '/',
    to: '/dennis/content/subjects/search'
  });

  const searchRoute = createRoute({
    getParentRoute: () => subjectsRoute,
    path: 'search',
    staticData: {name: 'Subject Lookup'},
    component: withRouter(SubjectSearch)
  });

  const newRoute = createRoute({
    getParentRoute: () => subjectsRoute,
    path: 'new',
    staticData: {name: 'New Subject'},
    component: layoutComponent(AuthoringSubject)
  });
  newRoute.addChildren([
    createRoute({
      getParentRoute: () => newRoute,
      path: '/',
      component: withRouter(AuthoringSubjectEditor)
    })
  ]);

  // Pathless layout (RR3 `<Route component={AuthoringSubjectWithQuery}>` with no path).
  const withQueryRoute = createRoute({
    getParentRoute: () => subjectsRoute,
    id: 'authoring-subject-with-query',
    component: layoutComponent(AuthoringSubjectWithQuery)
  });

  // `:subjectId` container (no component → TanStack renders <Outlet/>).
  const subjectIdRoute = createRoute({
    getParentRoute: () => withQueryRoute,
    path: '$subjectId'
  });
  subjectIdRoute.addChildren([
    redirectRoute({
      getParentRoute: () => subjectIdRoute,
      path: '/',
      to: '/dennis/content/subjects/$subjectId/subject'
    }),
    createRoute({
      getParentRoute: () => subjectIdRoute,
      path: 'subject',
      staticData: {name: 'Subject Editor'},
      component: withRouter(AuthoringSubjectEditor)
    }),
    createRoute({
      getParentRoute: () => subjectIdRoute,
      path: 'guides/{-$guideId}',
      component: withRouter(GuideEditor)
    })
  ]);

  withQueryRoute.addChildren([subjectIdRoute]);

  return subjectsRoute.addChildren([indexRedirect, searchRoute, newRoute, withQueryRoute]);
}
